import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Typography, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Modal
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReplayIcon from '@mui/icons-material/Replay';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { FindocsService } from '../../../services/dao/findocs-service';

const isValidDate = (d) => d && !isNaN(new Date(d).getTime());
const fmtDate = (d) => isValidDate(d) ? format(new Date(d), 'MM/dd/yyyy') : null;
const fmtMoney = (v) => v != null ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const CATEGORY_COLORS = {
    'Credit Card Payments': { bg: '#E8EDF2', color: '#141F2E' },
    'Shopping': { bg: '#ECFDF5', color: '#059669' },
    'Groceries': { bg: '#EFF6FF', color: '#2563EB' },
    'Cash Advances': { bg: '#FFF7ED', color: '#C2410C' },
    'Dining': { bg: '#FBF5EB', color: '#8B6914' },
};
const getCatStyle = (cat) => CATEGORY_COLORS[cat] || { bg: '#F1F5F9', color: '#475569' };

const PURPOSE_OPTIONS = [
    { value: 'tax:deductible', label: 'Tax Deductible', color: '#059669', bg: '#ECFDF5' },
    { value: 'tax:non-deductible', label: 'Tax Non-Deductible', color: '#6B7280', bg: '#F3F4F6' },
    { value: 'business:operating', label: 'Business Operating', color: '#2563EB', bg: '#EFF6FF' },
    { value: 'business:travel', label: 'Business Travel', color: '#7C3AED', bg: '#F5F3FF' },
    { value: 'business:meals', label: 'Business Meals', color: '#D97706', bg: '#FFFBEB' },
    { value: 'business:equipment', label: 'Business Equipment', color: '#0891B2', bg: '#ECFEFF' },
    { value: 'business:contractor', label: 'Business Contractor', color: '#4F46E5', bg: '#EEF2FF' },
    { value: 'personal:essential', label: 'Personal Essential', color: '#475569', bg: '#F1F5F9' },
    { value: 'personal:discretionary', label: 'Personal Discretionary', color: '#9333EA', bg: '#FAF5FF' },
    { value: 'personal:savings', label: 'Personal Savings', color: '#059669', bg: '#F0FDF4' },
];
const getPurposeStyle = (p) => PURPOSE_OPTIONS.find(o => o.value === p) || { label: p || '—', color: '#9CA3AF', bg: '#F9FAFB' };

const findocsService = new FindocsService();

const FindocDetailsCard = ({ findoc, highlightDescription = '' }) => {
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [reanalyzing, setReanalyzing] = useState(false);
    const [purposeAnchor, setPurposeAnchor] = useState(null);
    const [purposeIdx, setPurposeIdx] = useState(null);
    const highlightedRef = useCallback(node => {
        if (node) setTimeout(() => node.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }, []);

    const handleReanalyze = async () => {
        setReanalyzing(true);
        try { await findocsService.reanalyze(findoc._id); }
        catch (e) { /* ignore */ }
        setReanalyzing(false);
    };

    const lineItems = findoc.lineItems || [];
    const accountSummaries = findoc.accountSummaries || [];
    const filtered = useMemo(() => {
        const indexed = lineItems.map((li, idx) => ({ ...li, _idx: idx }));
        if (!search) return indexed;
        const q = search.toLowerCase();
        return indexed.filter(li =>
            (li.description || '').toLowerCase().includes(q) ||
            (li.category || '').toLowerCase().includes(q) ||
            (li.purpose || '').toLowerCase().includes(q)
        );
    }, [lineItems, search]);

    // Compute quick stats from line items
    const stats = useMemo(() => {
        let purchases = 0, payments = 0, other = 0;
        lineItems.forEach(li => {
            const amt = li.amount || 0;
            const cat = (li.category || '').toLowerCase();
            if (cat.includes('payment') || cat.includes('credit')) payments += amt;
            else if (cat.includes('cash advance')) other += amt;
            else purchases += amt;
        });
        return { purchases, payments, other };
    }, [lineItems]);

    // Group account summaries by account
    const groupedSummaries = useMemo(() => {
        const groups = {};
        accountSummaries.forEach(s => {
            const key = s.accountType || s.accountId || 'Account';
            if (!groups[key]) groups[key] = [];
            groups[key].push(s);
        });
        return groups;
    }, [accountSummaries]);

    const handleExport = () => {
        let tsv = "Date\tCategory\tPurpose\tDescription\tAmount\n";
        lineItems.forEach(li => {
            tsv += `${fmtDate(li.transactionDate) || ''}\t${li.category || ''}\t${li.purpose || ''}\t${li.description || ''}\t${li.amount || ''}\n`;
        });
        saveAs(new Blob([tsv], { type: 'text/tsv' }), `${findoc.institutionName || 'statement'}.tsv`);
    };

    const statementDate = fmtDate(findoc.statementDate);
    const paymentDate = fmtDate(findoc.paymentDate);
    const hasPdf = findoc.meta?.s3Key;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 1000, mx: 'auto' }}>
            {/* Back button */}
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none',
                    '&:hover': { background: 'rgba(99,102,241,0.06)', color: 'primary.main' } }}>
                Back
            </Button>

            {/* Summary card */}
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    {/* Bank icon */}
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, background: '#E8EDF2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AccountBalanceIcon sx={{ color: '#1F2F42', fontSize: '1.5rem' }} />
                    </Box>
                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {findoc.institutionName || 'Unknown Institution'}
                        </Typography>
                        {findoc.documentType && (
                            <Chip label={findoc.documentType === 'credit_card' ? 'Credit Card' : 'Bank Statement'}
                                size="small" sx={{ mb: 1, fontWeight: 600, fontSize: '0.7rem',
                                    background: findoc.documentType === 'credit_card' ? '#FEF3C7' : '#DBEAFE',
                                    color: findoc.documentType === 'credit_card' ? '#92400E' : '#1E40AF' }} />
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                            {findoc.statementDateRange && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Period: {findoc.statementDateRange}
                                </Typography>
                            )}
                            {paymentDate && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Payment Due: {paymentDate}
                                </Typography>
                            )}
                        </Box>
                        {findoc.institutionAddress && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {findoc.institutionAddress}
                            </Typography>
                        )}
                    </Box>
                    {/* Balance */}
                    {findoc.balance != null && (
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Balance</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: findoc.balance >= 0 ? '#059669' : '#DC2626' }}>
                                {fmtMoney(findoc.balance)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Quick stats */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                    {stats.purchases !== 0 && (
                        <Chip label={`Purchases: ${fmtMoney(stats.purchases)}`} size="small"
                            sx={{ fontWeight: 600, background: '#FEF2F2', color: '#DC2626' }} />
                    )}
                    {stats.payments !== 0 && (
                        <Chip label={`Payments/Credits: ${fmtMoney(stats.payments)}`} size="small"
                            sx={{ fontWeight: 600, background: '#ECFDF5', color: '#059669' }} />
                    )}
                    {stats.other !== 0 && (
                        <Chip label={`Cash Advances: ${fmtMoney(stats.other)}`} size="small"
                            sx={{ fontWeight: 600, background: '#FFF7ED', color: '#C2410C' }} />
                    )}
                </Box>
            </Paper>

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search transactions..." value={search}
                    onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /></InputAdornment> }} />
                <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExport}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>Export CSV</Button>
                <Button variant="outlined" size="small" startIcon={reanalyzing ? <CircularProgress size={14} /> : <ReplayIcon />}
                    disabled={reanalyzing} onClick={handleReanalyze}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>{reanalyzing ? 'Reanalyzing…' : 'Reanalyze'}</Button>
                {hasPdf && (
                    <Button variant="outlined" size="small" startIcon={pdfLoading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                        disabled={pdfLoading}
                        onClick={async () => {
                            setPdfLoading(true);
                            const url = await findocsService.getPdfUrl(findoc._id);
                            setPdfUrl(url);
                            setPdfLoading(false);
                            if (url) setPdfOpen(true);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>View PDF</Button>
                )}
            </Box>

            {/* Account Summaries */}
            {accountSummaries.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2, border: '1px solid rgba(0,0,0,0.06)', background: '#F8FAFC' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                        Account Summary
                    </Typography>
                    {Object.entries(groupedSummaries).map(([acctLabel, items]) => (
                        <Box key={acctLabel} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{acctLabel}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {items.map((s, i) => (
                                    <Chip key={i} label={`${s.description}: ${fmtMoney(s.amount)}`} size="small"
                                        sx={{ fontWeight: 600, fontSize: '0.75rem',
                                            background: s.description?.toLowerCase().includes('ending') ? '#EFF6FF' :
                                                s.description?.toLowerCase().includes('beginning') ? '#F1F5F9' :
                                                s.description?.toLowerCase().includes('deposit') || s.description?.toLowerCase().includes('credit') ? '#ECFDF5' :
                                                s.description?.toLowerCase().includes('withdrawal') || s.description?.toLowerCase().includes('debit') ? '#FEF2F2' : '#F1F5F9',
                                            color: s.description?.toLowerCase().includes('deposit') || s.description?.toLowerCase().includes('credit') ? '#059669' :
                                                s.description?.toLowerCase().includes('withdrawal') || s.description?.toLowerCase().includes('debit') ? '#DC2626' : '#475569'
                                        }} />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Transactions table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', borderBottom: '2px solid rgba(0,0,0,0.08)' } }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((li, i) => {
                            const date = fmtDate(li.transactionDate);
                            const amt = li.amount;
                            const catStyle = getCatStyle(li.category);
                            const isHighlighted = highlightDescription && li.description === highlightDescription;
                            return (
                                <TableRow key={i}
                                    ref={isHighlighted && !filtered.slice(0, i).some(x => x.description === highlightDescription) ? highlightedRef : undefined}
                                    sx={{ '&:nth-of-type(even)': { background: '#F8FAFC' }, '&:hover': { background: '#E8EDF2' },
                                        ...(isHighlighted ? { background: '#FEF9C3 !important', transition: 'background 2s ease' } : {}) }}>
                                    <TableCell sx={{ fontSize: '0.85rem', color: date ? 'text.primary' : 'text.secondary' }}>
                                        {date || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {li.category && (
                                            <Chip label={li.category} size="small"
                                                sx={{ fontSize: '0.7rem', fontWeight: 600, background: catStyle.bg, color: catStyle.color }} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(() => { const ps = getPurposeStyle(li.purpose); return (
                                            <Chip label={ps.label} size="small"
                                                onClick={(e) => { setPurposeAnchor(e.currentTarget); setPurposeIdx(li._idx); }}
                                                sx={{ fontSize: '0.65rem', fontWeight: 600, background: ps.bg, color: ps.color, cursor: 'pointer',
                                                    '&:hover': { opacity: 0.8 } }} />
                                        ); })()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{li.description}</TableCell>
                                    <TableCell align="right" sx={{
                                        fontSize: '0.85rem', fontWeight: 600,
                                        color: li.transactionType === 'debit' ? '#DC2626' : li.transactionType === 'credit' ? '#059669' : (amt > 0 ? '#DC2626' : amt < 0 ? '#059669' : 'text.primary'),
                                    }}>
                                        {fmtMoney(amt) || '—'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {/* Totals row */}
                        {findoc.balance != null && (
                            <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.1)', fontSize: '0.85rem' } }}>
                                <TableCell colSpan={4}>Ending Balance</TableCell>
                                <TableCell align="right">{fmtMoney(findoc.balance)}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu anchorEl={purposeAnchor} open={Boolean(purposeAnchor)}
                onClose={() => setPurposeAnchor(null)}>
                {PURPOSE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} dense
                        selected={purposeIdx != null && lineItems[purposeIdx]?.purpose === opt.value}
                        onClick={async () => {
                            setPurposeAnchor(null);
                            await findocsService.updateLineItemPurpose(findoc._id, purposeIdx, opt.value);
                            lineItems[purposeIdx].purpose = opt.value;
                            setPurposeIdx(null);
                        }}
                        sx={{ fontSize: '0.8rem', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2, textAlign: 'center' }}>
                {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
            </Typography>

            {/* PDF viewer */}
            <Modal open={pdfOpen} onClose={() => setPdfOpen(false)}>
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton sx={{ position: 'fixed', top: 12, right: 12, color: 'white', zIndex: 1 }} onClick={() => setPdfOpen(false)}><CloseIcon /></IconButton>
                    {pdfUrl && <iframe src={pdfUrl} title="Statement PDF" style={{ width: '90%', height: '90%', border: 'none', borderRadius: 8 }} />}
                </Box>
            </Modal>
        </Box>
    );
};

export default FindocDetailsCard;
